import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Brain,
  PlayCircle,
  Clock3,
  Layers3,
  Target,
  Zap,
  Focus,
  BarChart3,
  ArrowRight,
} from "lucide-react";

import "./TrainingModule.css";

import IgniteNavbar from "../../layout/IgniteNavbar";
import IgniteHeader from "../../layout/IgniteHeader";

const TrainingModule = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  // Hide website footer / external navbar if your app has one.
  useEffect(() => {
    const navbar = document.querySelector("header, .navbar");
    const footer = document.querySelector("footer");

    if (navbar) navbar.style.display = "none";
    if (footer) footer.style.display = "none";

    return () => {
      if (navbar) navbar.style.display = "";
      if (footer) footer.style.display = "";
    };
  }, []);

  const startTraining = () => {
    navigate("/short-term-memory-training");
  };

  return (
    <div className="ignite-training-module">
      <IgniteNavbar />

      <main className="ignite-training-module-main">
        <IgniteHeader
          title="Training Center"
          subtitle="Build your skills. Train smarter. Perform better."
        />

        {/* ==========================================
            HERO + WHY TRAIN
        ========================================== */}
        <section className="training-module-top-grid">

          {/* HERO */}
          <div className="stm-training-hero">

            <div className="stm-hero-content">

              <div className="stm-training-badge">
                <Brain size={16} />
                <span>COGNITIVE TRAINING</span>
              </div>

              <h2>
                Short Term
                <span>Memory Training</span>
              </h2>

              <p>
                Remember and recall card sequences to improve your
                memory, focus and mental agility. Start with easy
                levels and challenge yourself with higher levels.
              </p>

              <div className="stm-hero-actions">
                <button
                  type="button"
                  className="stm-start-btn"
                  onClick={startTraining}
                >
                  <PlayCircle size={21} />
                  <span>Start Training</span>
                  <ArrowRight size={19} />
                </button>

                <div className="stm-info-chip">
                  <Layers3 size={22} />
                  <div>
                    <span>Number of Cards</span>
                    <strong>3 – 10</strong>
                  </div>
                </div>

                <div className="stm-info-chip">
                  <Clock3 size={22} />
                  <div>
                    <span>Display Time</span>
                    <strong>2 – 7 sec</strong>
                  </div>
                </div>
              </div>

            </div>

            {/* PLAYING CARDS VISUAL */}
            <div className="stm-card-visual" aria-hidden="true">
              <div className="stm-glow stm-glow-one" />
              <div className="stm-glow stm-glow-two" />

              <div className="stm-playing-card stm-card-spade">
                <span>A</span>
                <strong>♠</strong>
              </div>

              <div className="stm-playing-card stm-card-heart">
                <span>♥</span>
                <strong>♥</strong>
              </div>

              <div className="stm-playing-card stm-card-club">
                <span>♣</span>
                <strong>♣</strong>
              </div>
            </div>

          </div>


          {/* WHY TRAIN */}
          <div className="stm-benefits-card">

            <div className="stm-benefits-title">
              <Brain size={22} />
              <h3>Why Train Your Memory?</h3>
            </div>

            <div className="stm-benefit-item">
              <div className="stm-benefit-icon green">
                <Focus size={21} />
              </div>

              <div>
                <h4>Improve Focus</h4>
                <p>
                  Enhance your concentration and attention span.
                </p>
              </div>
            </div>

            <div className="stm-benefit-item">
              <div className="stm-benefit-icon purple">
                <Brain size={21} />
              </div>

              <div>
                <h4>Boost Memory</h4>
                <p>
                  Strengthen your short-term memory and recall abilities.
                </p>
              </div>
            </div>

            <div className="stm-benefit-item">
              <div className="stm-benefit-icon orange">
                <Zap size={21} />
              </div>

              <div>
                <h4>Build Mental Agility</h4>
                <p>
                  Think faster and perform better in everyday tasks.
                </p>
              </div>
            </div>

            <div className="stm-benefit-item">
              <div className="stm-benefit-icon red">
                <BarChart3 size={21} />
              </div>

              <div>
                <h4>Level Up Gradually</h4>
                <p>
                  Start easy and challenge yourself with higher levels.
                </p>
              </div>
            </div>

          </div>
        </section>


        {/* ==========================================
            AVAILABLE TRAINING MODULE
        ========================================== */}
        <section className="stm-available-section">

          <div className="stm-section-heading">
            <div className="stm-section-heading-icon">
              <Target size={23} />
            </div>

            <div>
              <h2>Available Training Module</h2>
              <p>
                Start training and improve your cognitive skills.
              </p>
            </div>
          </div>


          <div className="stm-module-card">

            <div className="stm-module-left">

              <div className="stm-module-icon">
                <Brain size={31} />
              </div>

              <div className="stm-module-details">
                <div className="stm-module-title-row">
                  <h3>Short Term Memory</h3>
                  <span className="stm-new-badge">NEW</span>
                </div>

                <p>
                  Remember and recall card sequences to improve
                  your memory, focus and concentration.
                </p>

                <div className="stm-module-stats">

                  <div>
                    <Layers3 size={20} />
                    <span>
                      Cards
                      <strong>3 – 10</strong>
                    </span>
                  </div>

                  <div>
                    <Clock3 size={20} />
                    <span>
                      Display Time
                      <strong>2 – 7 sec</strong>
                    </span>
                  </div>

                  <div>
                    <BarChart3 size={20} />
                    <span>
                      Training
                      <strong>Multiple Levels</strong>
                    </span>
                  </div>

                </div>
              </div>

            </div>


            <button
              type="button"
              className="stm-module-start-btn"
              onClick={startTraining}
            >
              <PlayCircle size={20} />
              <span>Start Training</span>
              <ArrowRight size={19} />
            </button>

          </div>

        </section>

      </main>
    </div>
  );
};

export default TrainingModule;
